import { Link } from 'react-router-dom';
import { GithubIcon, LinkedinIcon, TwitterIcon } from '@/components/icons/BrandIcons';
import BrandName from '@/components/common/BrandName';

export default function LandingFooter() {
  const currentYear = new Date().getFullYear();

  return (
    <footer role="contentinfo" className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-8">
          {/* Brand */}
          <div className="col-span-1 sm:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <img src="/logo.svg" alt="CodePulse Logo" className="w-9 h-9" aria-hidden="true" />
              <BrandName className="text-xl font-bold text-white" />
            </div>
            <p className="text-gray-400 mb-4 max-w-md">
              Engineering analytics that help you ship faster, improve quality, and build healthier teams.
              Connect your GitHub and see insights in minutes.
            </p>
            <div className="flex gap-4">
              <a
                href="https://github.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Visit CodePulse on GitHub"
              >
                <GithubIcon className="w-5 h-5" aria-hidden="true" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Visit CodePulse on Twitter"
              >
                <TwitterIcon className="w-5 h-5" aria-hidden="true" />
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Visit CodePulse on LinkedIn"
              >
                <LinkedinIcon className="w-5 h-5" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Product */}
          <div>
            <h3 className="text-white font-semibold mb-4">Product</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/features" className="hover:text-white transition-colors">
                  All Features
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="hover:text-white transition-colors">
                  Pricing
                </Link>
              </li>
              <li>
                <a href="/#how-it-works" className="hover:text-white transition-colors">
                  How It Works
                </a>
              </li>
              <li>
                <Link to="/guides" className="hover:text-white transition-colors">
                  Guides
                </Link>
              </li>
              <li>
                <Link to="/tools" className="hover:text-white transition-colors">
                  Free Tools
                </Link>
              </li>
              <li>
                <Link to="/research" className="hover:text-white transition-colors">
                  Research
                </Link>
              </li>
              <li>
                <Link to="/how-it-works" className="hover:text-white transition-colors">
                  Documentation
                </Link>
              </li>
              <li>
                <Link to="/quiz" className="hover:text-white transition-colors">
                  Health Quiz
                </Link>
              </li>
            </ul>
          </div>

          {/* Features */}
          <div>
            <h3 className="text-white font-semibold mb-4">Features</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/features/cycle-time" className="hover:text-white transition-colors">
                  Cycle Time Analytics
                </Link>
              </li>
              <li>
                <Link to="/features/dora-metrics" className="hover:text-white transition-colors">
                  DORA Metrics
                </Link>
              </li>
              <li>
                <Link to="/features/review-network" className="hover:text-white transition-colors">
                  Review Network
                </Link>
              </li>
              <li>
                <Link to="/features/file-hotspots" className="hover:text-white transition-colors">
                  File Hotspots
                </Link>
              </li>
              <li>
                <Link to="/features/forecasting" className="hover:text-white transition-colors">
                  Delivery Forecasting
                </Link>
              </li>
              <li>
                <Link to="/features/space-framework" className="hover:text-white transition-colors">
                  SPACE Framework
                </Link>
              </li>
              <li>
                <Link to="/features/risky-changes" className="hover:text-white transition-colors">
                  Risk Detection
                </Link>
              </li>
              <li>
                <Link to="/features/executive-summary" className="hover:text-white transition-colors">
                  Executive Summary
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-white font-semibold mb-4">Company</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="hover:text-white transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <a
                  href="https://status.codepulsehq.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  System Status
                </a>
              </li>
              <li>
                <Link to="/contact" className="hover:text-white transition-colors">
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-white font-semibold mb-4">Legal</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/privacy" className="hover:text-white transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="hover:text-white transition-colors">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/dpa" className="hover:text-white transition-colors">
                  Data Processing Agreement
                </Link>
              </li>
              <li>
                <Link to="/security" className="hover:text-white transition-colors">
                  Data Security
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">
            &copy; {currentYear} Keep Pushing Forward Ltd, trading as CodePulse. Registered in England and Wales.
          </p>
          <p className="text-gray-400 text-sm">
            Based on{' '}
            <a
              href="https://dora.dev"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-400 hover:text-primary-300 underline"
            >
              DORA research
            </a>
            {' '}by Google
          </p>
        </div>
      </div>
    </footer>
  );
}
